import React from 'react';
import BrandChip, { Brand } from '../common/brandChip/brandChip';
import InfoBubble from '../common/infoBubble/infoBubble';
import s from './settingsComponent.module.scss';

interface Props {
  /**
   * The service's logo. Omitted where the button itself already carries the
   * logo - Google's branded sign-in button - so the mark is not shown twice.
   */
  brand?: Brand;
  /** An (i) beside the logo, where there is something to say about the service. */
  info?: { label: string; text: React.ReactNode };
  /**
   * Whose account it is, or what state it is in. Not "connected": a Logout
   * button says that already. Left out entirely where there is nothing to
   * add, so the row stays one line.
   */
  status?: React.ReactNode;
  /** The row's buttons. */
  children?: React.ReactNode;
}

/**
 * One service in Settings: its logo, what its account is, then its buttons,
 * on one line where the width allows. Shared by the Storage and Streaming
 * groups so both read the same way.
 */
export default function ServiceRow({ brand, info, status, children }: Props) {
  const bubble = info && <InfoBubble label={info.label}>{info.text}</InfoBubble>;

  return (
    <li className={s.serviceRow}>
      {brand && (
        <span className={s.serviceBrand}>
          <BrandChip brand={brand} />
          {bubble}
        </span>
      )}
      {status && <span className={s.serviceStatus}>{status}</span>}
      {children && (
        <span className={s.serviceButtons}>
          {children}
          {/*
            With no logo of ours, the row's own control is the brand - the
            Google sign-in button is Google's artwork - so it takes the first
            column and the (i) follows it, rather than the (i) sitting alone
            in an empty logo slot.
          */}
          {!brand && bubble}
        </span>
      )}
    </li>
  );
}
